import { closeSnackbar, enqueueSnackbar, type EnqueueSnackbar } from 'notistack';
import { track } from '@/shared/analytics';

function deriveFeatureArea(): string {
  const segments = window.location.pathname.split('/').filter(Boolean);
  // eslint-disable-next-line i18next/no-literal-string -- analytics property value derived from the route path
  return segments[0] ?? 'root';
}

function normalizeErrorCode(message: unknown): string {
  // eslint-disable-next-line i18next/no-literal-string -- analytics error_code slug
  if (typeof message !== 'string') return 'unknown';
  return (
    message
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      // eslint-disable-next-line i18next/no-literal-string -- analytics error_code slug
      .slice(0, 64) || 'unknown'
  );
}

type EnqueueSnackbarParams = Parameters<EnqueueSnackbar>;
type MessageParam = EnqueueSnackbarParams[0];
type OptionsParam = NonNullable<EnqueueSnackbarParams[1]>;

/**
 * Action button configuration for notifications.
 */
type NotificationAction = {
  /** Button label text */
  label: string;
  /** Click handler */
  onClick: () => void;
};

/**
 * Notification configuration options.
 */
type NotificationOptions = {
  /** Additional context below the main message */
  description?: string;
  /** Custom action button with label and onClick handler */
  action?: NotificationAction;
  /** Prevents auto-dismiss and shows close button */
  persist?: boolean;
  /** Prevents duplicate notifications */
  preventDuplicate?: boolean;
  /** Custom notification ID for programmatic control */
  key?: string;
  /** Custom auto-hide duration in ms (default: 6000) */
  autoHideDuration?: number;
  /**
   * Custom anchor origin for notification position.
   * @default { horizontal: left, vertical: bottom }
   */
  anchorOrigin?: OptionsParam['anchorOrigin'];
};

/**
 * Centralized notification service for displaying user feedback.
 */
class NotificationService {
  /**
   * Show success notification (green).
   * Auto-dismisses after 6s unless persist: true.
   *
   * @param message - Notification message text
   * @param options - Optional configuration (see {@link NotificationOptions})
   * @returns Notification key for programmatic control
   */
  public showSuccess(message: MessageParam, options?: NotificationOptions) {
    return this.show(message, {
      ...options,
      variant: 'success',
    });
  }

  /**
   * Show error notification (red).
   * Auto-dismisses after 6s unless persist: true.
   *
   * @param message - Notification message text
   * @param options - Optional configuration (see {@link NotificationOptions})
   * @returns Notification key for programmatic control
   */
  public showError(message: MessageParam, options?: NotificationOptions) {
    track('feature_error_displayed', {
      feature_area: deriveFeatureArea(),
      error_code: normalizeErrorCode(message),
    });
    return this.show(message, {
      ...options,
      variant: 'error',
    });
  }

  /**
   * Show info notification (blue).
   * Auto-dismisses after 6s unless persist: true.
   *
   * @param message - Notification message text
   * @param options - Optional configuration (see {@link NotificationOptions})
   * @returns Notification key for programmatic control
   */
  public showInfo(message: MessageParam, options?: NotificationOptions) {
    return this.show(message, {
      ...options,
      variant: 'info',
    });
  }

  /**
   * Show warning notification (yellow).
   * Auto-dismisses after 6s unless persist: true.
   *
   * @param message - Notification message text
   * @param options - Optional configuration (see {@link NotificationOptions})
   * @returns Notification key for programmatic control
   */
  public showWarning(message: MessageParam, options?: NotificationOptions) {
    return this.show(message, {
      ...options,
      variant: 'warning',
    });
  }

  /**
   * Show notification with custom variant.
   *
   * @param message - Notification message text
   * @param options - Optional configuration including variant
   * @returns Notification key for programmatic control
   */
  public show(
    message: MessageParam,
    options?: NotificationOptions & Pick<OptionsParam, 'variant'>,
  ) {
    const { action, ...notistackOptions } = options || {};

    const key = enqueueSnackbar(message, {
      ...notistackOptions,
      ...(action && {
        // NotificationProvider reads this structured config and renders it via
        // SonnerToast's own action button.
        actionConfig: action,
      }),
    });
    return key;
  }

  /**
   * Close notification by key. Omit key to close all.
   *
   * @param key - Optional notification key returned from show methods
   */
  public close: typeof closeSnackbar = (key) => closeSnackbar(key);
}

export const notificationService = new NotificationService();
